import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { GeniusData, GeniusFixtureRoster } from './types'

export interface GeniusState {
  status: GeniusData
  disabled: boolean
  fixtureDate: Date
  data: GeniusData
  ignoredPlayerIds: string[]
  fetchingPlayerIds: string[]
  roster: GeniusFixtureRoster | null
}

export const emptyGeniusData: GeniusData = {
  currentFixture: {
    certainty: 0,
    geniusData: '',
    geniusID: '',
    geniusTrackingId: '',
    matchedAgainst: '',
    sportableId: '',
    errors: []
  },
  playerIds: {},
  teamIds: {}
}

export const emptyGeniusPlayerResult = {
  certainty: 0,
  geniusData: '',
  geniusID: '',
  geniusTrackingId: '',
  matchedAgainst: '',
  sportableId: '',
  errors: []
}

const initialGeniusState: GeniusState = {
  status: emptyGeniusData,
  disabled: false,
  fixtureDate: new Date(),
  data: emptyGeniusData,
  ignoredPlayerIds: [],
  fetchingPlayerIds: [],
  roster: null
}

export const geniusSlice = createSlice({
  name: 'genius',
  initialState: initialGeniusState,
  reducers: {
    setGeniusStatus: (state, action: PayloadAction<GeniusData>) => {
      state.data = action.payload
    },
    setDisabled: (state, action: PayloadAction<boolean>) => {
      state.disabled = action.payload
    },
    setFixtureDate: (state, action) => {
      state.fixtureDate = action.payload
    },
    updateFixtureFromAPIResponse: (
      state,
      action: PayloadAction<GeniusData>
    ) => {
      state.data = action.payload
    },
    updatePlayersFromAPIResponse: (
      state,
      action: PayloadAction<GeniusData['playerIds']>
    ) => {
      for (const key in action.payload) {
        state.data.playerIds[key] =
          action.payload[key] || state.data.playerIds[key]
      }
    },
    updateIgnoredPlayerId: (state, action: PayloadAction<string>) => {
      const playerId = action.payload
      if (state.ignoredPlayerIds.includes(playerId)) {
        state.ignoredPlayerIds = state.ignoredPlayerIds.filter(
          (id) => id !== playerId
        )
      } else {
        state.ignoredPlayerIds.push(playerId)
      }
    },
    addFetchingPlayerIds: (state, action: PayloadAction<string[]>) => {
      state.fetchingPlayerIds = [...state.fetchingPlayerIds, ...action.payload]
    },
    removeFetchingPlayerIds: (state, action: PayloadAction<string[]>) => {
      state.fetchingPlayerIds = state.fetchingPlayerIds.filter(
        (id) => !action.payload.includes(id)
      )
    },
    removeGeniusData: (state) => {
      state.data = emptyGeniusData
    },
    setRoster: (state, action: PayloadAction<GeniusFixtureRoster>) => {
      state.roster = action.payload
    }
  }
})

export const reducer = geniusSlice.reducer

export const {
  setGeniusStatus,
  setDisabled,
  setFixtureDate,
  updateFixtureFromAPIResponse,
  updatePlayersFromAPIResponse,
  updateIgnoredPlayerId,
  addFetchingPlayerIds,
  removeFetchingPlayerIds,
  removeGeniusData,
  setRoster
} = geniusSlice.actions
